import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  IStudentListQueryParams,
  TStudentDetails,
  TStudentPrimary,
} from '../../../../apis/students/students.interfaces';
import { useQueryClient } from '@tanstack/react-query';
import { getStudentListKey, useStudentListHook } from '../hooks/useStudentListHook';
import { useStudentDetailsByIdHook } from '../hooks/useStudentDetailsByIdHook';
import { useStudentFiltersState } from './studentFilters.context';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import {
  IClassAndFeeStructureSchemaResponse,
  IFeeStructureParams,
  IFeeStructureResponse,
} from '../../../../apis/institute/institute.interfaces';
import { useFeeStructureHook } from '../../institute/hooks/useFeeStructureHook';
import { useGradeAndFeeStructureHook } from '../../institute/hooks/useGradeAndFeeStructureHook';
import isAppWithMock from 'src/utils/isAppWithMock';
import { shouldEnableStudentListQuery } from '../utils/studentListApiFilters';

type TStudentsState = {
  errorMessage?: string;
  isLoading: boolean;
  allStudents: TStudentPrimary[];
  filteredStudents: TStudentPrimary[];
  simplifiedStudentObject: Record<string, any>;
  paginationParams: TPaginationParams;
  totalCount: number;
  selectedStudentId: string | null;
  selectedStudent: TStudentDetails | null;
  gradeAndFeeStructure: IClassAndFeeStructureSchemaResponse | null;
  feeStructureParams: IFeeStructureParams | null;
  feeStructure: IFeeStructureResponse | null;
  feeStructureLoading: boolean;
};

enum ACTION_TYPE {
  SET_STUDENTS = 'SET_STUDENTS',
  SET_FILTERED_STUDENTS = 'SET_FILTERED_STUDENTS',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_PAGINATION_PARAMS = 'SET_PAGINATION_PARAMS',
  SET_SELECTED_STUDENT_ID = 'SET_SELECTED_STUDENT_ID',
  SET_SELECTED_STUDENT = 'SET_SELECTED_STUDENT',
  SET_GRADE_AND_FEE_STRUCTURE = 'SET_GRADE_AND_FEE_STRUCTURE',
}

type Action =
  | {
    type: ACTION_TYPE.SET_STUDENTS;
    payload: { students: TStudentPrimary[]; totalCount: number };
  }
  | { type: ACTION_TYPE.SET_FILTERED_STUDENTS; payload: TStudentPrimary[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_PAGINATION_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.SET_SELECTED_STUDENT_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_STUDENT; payload: TStudentDetails | null }
  | {
    type: ACTION_TYPE.SET_GRADE_AND_FEE_STRUCTURE;
    payload: IClassAndFeeStructureSchemaResponse | null;
  };

const initialState: TStudentsState = {
  allStudents: [],
  filteredStudents: [],
  errorMessage: undefined,
  isLoading: false,
  simplifiedStudentObject: {},
  paginationParams: {
    page: 1,
    limit: 10,
  },
  totalCount: 0,
  selectedStudentId: null,
  selectedStudent: null,
  gradeAndFeeStructure: null,
  feeStructureParams: null,
  feeStructure: null,
  feeStructureLoading: false,
};

const StudentsContext = createContext<TStudentsState | undefined>(undefined);
const StudentsDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const studentsReducer = (
  state: TStudentsState,
  action: Action
): TStudentsState => {
  switch (action.type) {
    case ACTION_TYPE.SET_STUDENTS:
      return {
        ...state,
        allStudents: action.payload.students,
        filteredStudents: action.payload.students,
        totalCount: action.payload.totalCount,
      };
    case ACTION_TYPE.SET_FILTERED_STUDENTS:
      return {
        ...state,
        filteredStudents: action.payload,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_PAGINATION_PARAMS:
      return { ...state, paginationParams: action.payload };
    case ACTION_TYPE.SET_SELECTED_STUDENT:
      return { ...state, selectedStudent: action.payload };
    case ACTION_TYPE.SET_SELECTED_STUDENT_ID:
      return { ...state, selectedStudentId: action.payload };
    case ACTION_TYPE.SET_GRADE_AND_FEE_STRUCTURE:
      return { ...state, gradeAndFeeStructure: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

const deriveFeeStructureParams = (
  student: TStudentDetails | null
): IFeeStructureParams | null => {
  if (!student?.gradeId || !student?.section || !student?.category) return null;
  return {
    grade: String(student.gradeId),
    section: String(student.section),
    category: String(student.category),
  };
};

const getSelectedFilterValue = (value?: string): string | undefined => {
  const selectedValue = value?.trim();
  return selectedValue && selectedValue !== 'select' ? selectedValue : undefined;
};

export const StudentsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(studentsReducer, initialState);
  const filters = useStudentFiltersState();
  const withMock = isAppWithMock();
  const { data: classFeesData } = useGradeAndFeeStructureHook({
    enabled: !withMock,
  });
  const { studentId: urlStudentId } = useParams<{ studentId: string }>();
  const location = useLocation();
  const isDetailsRoute = location.pathname.includes('/details');

  const studentIdForDetails = isDetailsRoute ? urlStudentId ?? state.selectedStudentId : state.selectedStudentId;
  const { data: studentFromDetails } = useStudentDetailsByIdHook(
    studentIdForDetails ?? '',
    { enabled: !!studentIdForDetails && isDetailsRoute }
  );

  const studentForFeeParams = state.selectedStudent ?? studentFromDetails ?? null;
  const feeStructureParams = useMemo(
    () => deriveFeeStructureParams(studentForFeeParams),
    [studentForFeeParams]
  );

  const { data: feeStructure, isLoading: feeStructureLoading } =
    useFeeStructureHook(feeStructureParams, {
      enabled: !!feeStructureParams?.grade && !!feeStructureParams?.section && !!feeStructureParams?.category,
    });

  const selectedClassId = getSelectedFilterValue(filters.grade);
  const selectedSectionId = getSelectedFilterValue(filters.section);

  const queryParams = useMemo<IStudentListQueryParams>(() => {
    const params: IStudentListQueryParams = {
      page: state.paginationParams.page,
      limit: state.paginationParams.limit,
    };
    if (selectedClassId) {
      params.classId = selectedClassId;
    }
    if (selectedSectionId) {
      params.sectionId = selectedSectionId;
    }
    return params;
  }, [
    state.paginationParams.page,
    state.paginationParams.limit,
    selectedClassId,
    selectedSectionId,
  ]);

  const listQueryEnabled = shouldEnableStudentListQuery({
    selectedClassId,
    withMock,
    classFeesData,
  });

  const { data: studentListResponse, isLoading } = useStudentListHook(
    queryParams,
    { enabled: listQueryEnabled }
  );

  // Reset pagination to page 1 when grade or section filter changes
  const prevGradeRef = useRef(filters.grade);
  const prevSectionRef = useRef(filters.section);

  useEffect(() => {
    const gradeChanged = prevGradeRef.current !== filters.grade;
    const sectionChanged = prevSectionRef.current !== filters.section;
    prevGradeRef.current = filters.grade;
    prevSectionRef.current = filters.section;

    if (gradeChanged || sectionChanged) {
      dispatch({
        type: ACTION_TYPE.SET_PAGINATION_PARAMS,
        payload: { page: 1, limit: state.paginationParams.limit || 10 },
      });
    }
  }, [filters.grade, filters.section, state.paginationParams.limit]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (studentListResponse) {
      dispatch({
        type: ACTION_TYPE.SET_STUDENTS,
        payload: {
          students: studentListResponse.data,
          totalCount: studentListResponse.total,
        },
      });
    }
  }, [studentListResponse]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading,
      feeStructureParams,
      feeStructure: feeStructure ?? null,
      feeStructureLoading,
    }),
    [state, isLoading, feeStructureParams, feeStructure, feeStructureLoading]
  );

  return (
    <StudentsContext.Provider value={value}>
      <StudentsDispatchContext.Provider value={dispatch}>
        {children}
      </StudentsDispatchContext.Provider>
    </StudentsContext.Provider>
  );
};

export const useStudentsActions = () => {
  const dispatch = useStudentsDispatch();
  const queryClient = useQueryClient();

  const updatePaginationParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_PAGINATION_PARAMS, payload: params });
  };

  // Invalidate every cached student-list query (any page/filter params) so the
  // grid refetches — e.g. after a new student is added.
  const refreshStudentList = () => {
    queryClient.invalidateQueries({ queryKey: [getStudentListKey] });
  };

  const setSelectedStudentId = (id: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_STUDENT_ID, payload: id });
  };

  const setSelectedStudent = (student: TStudentDetails | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_STUDENT, payload: student });
  };

  const setGradeAndFeeStructure = (
    structure: IClassAndFeeStructureSchemaResponse | null
  ) => {
    dispatch({
      type: ACTION_TYPE.SET_GRADE_AND_FEE_STRUCTURE,
      payload: structure,
    });
  };

  return {
    updatePaginationParams,
    setSelectedStudentId,
    setSelectedStudent,
    setGradeAndFeeStructure,
    refreshStudentList,
  };
};

export const useStudentsState = () => {
  const context = useContext(StudentsContext);
  if (context === undefined) {
    throw new Error('useStudentsState must be used within a StudentsProvider');
  }
  return context;
};

export const useStudentsDispatch = () => {
  const context = useContext(StudentsDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useStudentsDispatch must be used within a StudentsProvider'
    );
  }
  return context;
};
